import { convertAmount } from '@/lib/currency/convert'
import { formatCurrency, minorUnitsOf } from '@/lib/currency/format'
import { CANONICAL_CURRENCY, type DisplayCurrency } from '@/lib/currency/map'
import { dateFromIso } from '@/i18n/calendar'
import { localeFormatter } from '@/i18n/format'

/**
 * The locale and display currency a surface states its prices in — the pair every money
 * rendering needs, carried together so a call site cannot supply one and forget the other
 * (which is how a converted peso amount ends up labelled in dollars).
 *
 * Both come from the request: the locale from the URL's path prefix, the currency from
 * `renderedCurrency()` — the currency the page's amounts were ACTUALLY stated in, which
 * is the selected one only when a rate was available to convert at.
 */
export interface MoneyFormat {
    locale: string
    currency: DisplayCurrency
}

/**
 * Everything a surface needs to state a canonical fare: how to format it, and the rate to
 * convert it at (`undefined` — state it canonical).
 *
 * Carried as one value, and threaded as a single prop, because a page's surfaces must agree:
 * one page never mixes canonical and converted amounts, and it is canonical outright while
 * *any* of its prices is unconverted. Two independent
 * props are two chances to hand one surface a rate and its neighbour none, which is that
 * violation expressed as a plumbing mistake. The pair is safe by construction when the
 * route derives `currency` from the rate via `renderedCurrency`.
 */
export interface FareDisplay {
    money: MoneyFormat
    rate: number | undefined
}

/**
 * The display decision for a surface that states **canonical** amounts: no rate, so nothing
 * converts and Intl qualifies the amount (`USD 25`) on any locale whose own currency is
 * something else.
 *
 * The locale is still the request's, and that is the load-bearing half: deciding the
 * currency is canonical must not decide the locale with it. A canonical amount formatted at
 * a hard-coded `en-us` emits a bare `$25`, and on es-mx `$` IS the peso sign — a 25-peso
 * claim for a USD 25 fare.
 *
 * Shared rather than rebuilt per surface so no two callers express one decision two ways.
 * Not for a surface that formats through `formatPriceWhole`: `formatFare` preserves an
 * authored fare's cents, so a surface whose live-read fares carry cents but display whole
 * units builds the `money` half directly.
 */
export function canonicalFare(locale: string): FareDisplay {
    return { money: { locale, currency: CANONICAL_CURRENCY }, rate: undefined }
}

/**
 * Formats a price with the minor units its currency has (e.g. "$12.00", "¥1,235").
 */
export function formatPrice(price: number, { locale, currency }: MoneyFormat): string {
    return formatCurrency(price, currency, locale)
}

/**
 * Formats a price without minor units (e.g. "$12").
 */
export function formatPriceWhole(price: number, { locale, currency }: MoneyFormat): string {
    return formatCurrency(price, currency, locale, { fractionDigits: 0 })
}

/**
 * An amount rounded half up to `digits` decimal places. The scaled amount goes through 12
 * significant digits first because binary floating point stores a half unit just under or
 * over it — 61.5 × 18.13 is `1114.99499…`, which a bare `Math.round` would take down to
 * `1114.99`.
 */
const roundHalfUp = (amount: number, digits: number) => {
    const scale = 10 ** digits
    return Math.round(Number((amount * scale).toPrecision(12))) / scale
}

/**
 * Renders a canonical-currency fare, converting first when a rate is supplied.
 *
 * `convertAmount` deliberately returns an exact product — minor-unit precision is the
 * rendering surface's choice, not the conversion layer's — and this is that choice, held
 * in one place so the surfaces of a single page cannot disagree about it. One rule holds
 * whether or not the amount was converted: an amount with minor units states them, and a
 * whole amount gains no invented `.00`. A published $1.75 farebox rate renders `$1.75`, and
 * the same fare converted at 16.96 renders `$29.68`.
 *
 * Both paths go through the app's single price formatter, so the glyph and grouping are the
 * locale's (`USD 25` where a bare `$` would read as pesos, `$3,052.80` where it would not).
 *
 * Takes the whole `FareDisplay` rather than its two halves for the reason the type exists:
 * the currency labels the amount while the rate decides whether it was converted, so a
 * caller passing one surface's `money` with another's `rate` renders a raw canonical amount
 * under a peso sign — the same misread, silently and past `tsc`, since both
 * halves are structurally trivial. Splitting the pair at the call boundary would undo the
 * guarantee threading it as one prop buys.
 */
export function formatFare(amount: number, { money, rate }: FareDisplay): string {
    // Whole-or-not and the digits shown come from one rounding, so they cannot disagree.
    const stated = roundHalfUp(convertAmount(amount, rate), minorUnitsOf(money.currency))
    return Number.isInteger(stated) ? formatPriceWhole(stated, money) : formatPrice(stated, money)
}

/**
 * Renders a canonical-currency fare as a "from" fare: whole units, rounded half up, in
 * every currency. Rounded once, after converting where a rate is supplied — rounding
 * before would state 8.23 at 1.9 as 15 rather than the 16 that 15.637 is.
 */
export function formatWholeFare(amount: number, { money, rate }: FareDisplay): string {
    return formatPriceWhole(roundHalfUp(convertAmount(amount, rate), 0), money)
}

/**
 * Formats a `YYYY-MM-DD` date string as a short display date (e.g. "Thu, Feb 12").
 *
 * SSR-safe by construction (invariants.md#ssr-safe-rendered-dates): the `Date` is built
 * from explicit Y/M/D parts **at UTC** and read back through a formatter pinned to UTC,
 * so the rendered weekday/month/day equals the input string's calendar date in *every*
 * timezone — identical on the server-zone SSR render and the browser-zone client render.
 * Do NOT switch to `new Date(iso)` (which parses to an instant the local zone then
 * re-projects) or drop the UTC pin: either makes the output timezone-dependent and
 * reintroduces a React #418 hydration mismatch. Input must be a `YYYY-MM-DD` string,
 * never the ambient clock.
 *
 * Out of contract, nothing: absent and unreadable render the same empty string, and the
 * literal "Invalid date" is never rendered. It conflates
 * two states, which is the accepted cost — a marker on a page is the defect this avoids,
 * not the safe fallback.
 */
export function formatShortDate(iso: string, locale: string): string {
    const date = dateFromIso(iso)
    if (!date) {
        return ''
    }
    return localeFormatter(locale).dateTime(date, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * Formats duration in minutes to a human-readable string (e.g., "2h 30m").
 * Omits the hours part if zero, and the minutes part if zero.
 */
export function formatDuration(minutes: number): string {
    // round to whole minutes first — facts carries raw float durations (e.g. 119.48 min), so
    // an unrounded remainder would render "1h 59.48m" instead of "2h".
    const whole = Math.round(minutes)
    const hours = Math.floor(whole / 60)
    const mins = whole % 60
    if (hours === 0) {
        return `${mins}m`
    }
    if (mins === 0) {
        return `${hours}h`
    }
    return `${hours}h ${mins}m`
}
